import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import PatientCareTeam from '@/components/PatientCareTeam';
import {
  Heart, Calendar, Pill, FileText, Activity, Video,
  MessageSquare, Bell, User, Clock, ChevronRight, Users
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PatientDashboardPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState({
    upcomingVisits: [],
    medications: [],
    recentActivities: [],
    notifications: []
  });

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token') || localStorage.getItem('hospice_token');
    return { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
  };

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      // Fetch patient-specific data
      const [visitsRes, medsRes] = await Promise.all([
        fetch(`${API_URL}/api/visits/upcoming`, { headers: getAuthHeaders() }).catch(() => null),
        fetch(`${API_URL}/api/medications/patient`, { headers: getAuthHeaders() }).catch(() => null)
      ]);

      // Use mock data if API fails
      setDashboardData({
        upcomingVisits: visitsRes?.ok ? (await visitsRes.json()).visits : [
          { id: 1, type: 'Nursing Visit', date: '2026-01-22', time: '10:00 AM', staff: 'Sarah Johnson, RN' },
          { id: 2, type: 'Aide Visit', date: '2026-01-23', time: '2:00 PM', staff: 'Maria Garcia, CNA' },
          { id: 3, type: 'Social Work', date: '2026-01-25', time: '11:00 AM', staff: 'David Lee, MSW' }
        ],
        medications: medsRes?.ok ? (await medsRes.json()).medications : [
          { name: 'Morphine Sulfate', dosage: '10mg', frequency: 'Every 4 hours as needed', purpose: 'Pain management' },
          { name: 'Lorazepam', dosage: '0.5mg', frequency: 'Every 6 hours as needed', purpose: 'Anxiety relief' },
          { name: 'Docusate Sodium', dosage: '100mg', frequency: 'Twice daily', purpose: 'Bowel care' }
        ],
        recentActivities: [
          { type: 'Visit', description: 'Nursing assessment completed', date: '2026-01-20', staff: 'Jennifer Adams, RN' },
          { type: 'Order', description: 'Medication refill processed', date: '2026-01-19' },
          { type: 'Call', description: 'Phone check-in completed', date: '2026-01-18', staff: 'Lisa Thompson, SW' }
        ],
        notifications: []
      });
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8" data-testid="patient-dashboard">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Heart className="h-7 w-7 text-teal-600" />
            {t('patientDashboard.welcome', 'Welcome')}, {user?.name || t('patientDashboard.patient', 'Patient')}
          </h1>
          <p className="text-gray-600 mt-1">{t('patientDashboard.subtitle', 'Your hospice care dashboard')}</p>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <Button variant="outline" className="h-20 flex flex-col gap-2" onClick={() => navigate('/family-portal')}>
            <Users className="h-5 w-5 text-rose-500" />
            <span className="text-sm">{t('patientDashboard.familyPortal', 'Family Portal')}</span>
          </Button>
          <Button variant="outline" className="h-20 flex flex-col gap-2">
            <MessageSquare className="h-5 w-5 text-teal-600" />
            <span className="text-sm">{t('patientDashboard.messageTeam', 'Message Team')}</span>
          </Button>
          <Button variant="outline" className="h-20 flex flex-col gap-2">
            <Video className="h-5 w-5 text-blue-600" />
            <span className="text-sm">{t('patientDashboard.telehealth', 'Telehealth')}</span>
          </Button>
          <Button variant="outline" className="h-20 flex flex-col gap-2">
            <Calendar className="h-5 w-5 text-purple-600" />
            <span className="text-sm">{t('patientDashboard.viewSchedule', 'View Schedule')}</span>
          </Button>
          <Button variant="outline" className="h-20 flex flex-col gap-2" onClick={() => navigate('/notifications')}>
            <Bell className="h-5 w-5 text-amber-600" />
            <span className="text-sm">{t('patientDashboard.notifications', 'Notifications')}</span>
          </Button>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid grid-cols-4 w-full max-w-lg">
            <TabsTrigger value="overview">{t('patientDashboard.overview', 'Overview')}</TabsTrigger>
            <TabsTrigger value="care-team">{t('patientDashboard.careTeam', 'Care Team')}</TabsTrigger>
            <TabsTrigger value="medications">{t('patientDashboard.medications', 'Medications')}</TabsTrigger>
            <TabsTrigger value="schedule">{t('patientDashboard.schedule', 'Schedule')}</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Upcoming Visits */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Calendar className="h-5 w-5 text-teal-600" />
                    {t('patientDashboard.upcomingVisits', 'Upcoming Visits')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {dashboardData.upcomingVisits.length === 0 ? (
                    <p className="text-gray-500 text-center py-4">{t('patientDashboard.noUpcomingVisits', 'No upcoming visits scheduled')}</p>
                  ) : (
                    <div className="space-y-3">
                      {dashboardData.upcomingVisits.slice(0, 3).map((visit) => (
                        <div key={visit.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div>
                            <p className="font-medium text-sm">{visit.type}</p>
                            <p className="text-xs text-gray-500">{visit.staff}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-medium">{visit.date}</p>
                            <p className="text-xs text-gray-500">{visit.time}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Recent Activity */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Activity className="h-5 w-5 text-blue-600" />
                    {t('patientDashboard.recentActivity', 'Recent Activity')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {dashboardData.recentActivities.map((activity, idx) => (
                      <div key={idx} className="flex items-start gap-3 p-2 border-b last:border-0">
                        <div className="w-2 h-2 bg-teal-500 rounded-full mt-2"></div>
                        <div>
                          <p className="text-sm">{activity.description}</p>
                          <p className="text-xs text-gray-500">{activity.date}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Medications Summary */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Pill className="h-5 w-5 text-purple-600" />
                  {t('patientDashboard.currentMedications', 'Current Medications')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {dashboardData.medications.slice(0, 3).map((med, idx) => (
                    <div key={idx} className="p-3 border rounded-lg">
                      <p className="font-medium">{med.name}</p>
                      <p className="text-sm text-gray-600">{med.dosage}</p>
                      <p className="text-xs text-gray-500 mt-1">{med.frequency}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Care Team Tab */}
          <TabsContent value="care-team">
            <PatientCareTeam />
          </TabsContent>

          {/* Medications Tab */}
          <TabsContent value="medications">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Pill className="h-5 w-5 text-purple-600" />
                  {t('patientDashboard.yourMedications', 'Your Medications')}
                </CardTitle>
                <CardDescription>{t('patientDashboard.medicationsDescription', 'Current medication list managed by your care team')}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {dashboardData.medications.map((med, idx) => (
                    <div key={idx} className="p-4 border rounded-lg">
                      <div className="flex justify-between items-start">
                        <div>
                          <h4 className="font-medium">{med.name}</h4>
                          <p className="text-sm text-gray-600">{t('medications.dosage')}: {med.dosage}</p>
                          <p className="text-sm text-gray-600">{t('medications.frequency')}: {med.frequency}</p>
                        </div>
                        <Badge variant="outline">{med.purpose}</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Schedule Tab */}
          <TabsContent value="schedule">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-teal-600" />
                  {t('patientDashboard.visitSchedule', 'Visit Schedule')}
                </CardTitle>
                <CardDescription>{t('patientDashboard.visitScheduleDescription', 'Upcoming visits from your care team')}</CardDescription>
              </CardHeader>
              <CardContent>
                {dashboardData.upcomingVisits.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Calendar className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                    <p>{t('patientDashboard.noVisitsScheduled', 'No visits scheduled')}</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {dashboardData.upcomingVisits.map((visit) => (
                      <div key={visit.id} className="flex items-center justify-between p-4 border rounded-lg">
                        <div className="flex items-center gap-4">
                          <div className="p-2 bg-teal-100 rounded-lg">
                            <Calendar className="h-5 w-5 text-teal-600" />
                          </div>
                          <div>
                            <p className="font-medium">{visit.type}</p>
                            <p className="text-sm text-gray-500">{visit.staff}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-medium">{visit.date}</p>
                          <p className="text-sm text-gray-500">{visit.time}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default PatientDashboardPage;
